import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  Refresh as RefreshIcon,
  ContentCopy as DupeIcon,
} from '@mui/icons-material';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import ExpensesTable from '../../components/Table/ExpensesTable';
import { useVehicle } from './FuelSetting/hooks/useVehicle';
import { useFuelClaim } from './FuelSetting/hooks/useFuelClaim';
import VehicleListDialog from './FuelSetting/VehicleListDialog';
import VehicleFormDialog from './FuelSetting/VehicleFormDialog';
import FuelClaimDialog from './FuelSetting/FuelClaimDialog';

export default function FuelSetting({ title, subtitle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const { vehicleFuelSetting } = useSelector((state) => state.vehicleFuelSetting);
  const employees = useSelector((state) => state.employee?.rows || []);
  const projects = useSelector((state) => state.project?.rows || []);
  const budgets = useSelector((state) => state.budget?.rows || []);
  const customers = useSelector((state) => state.customer?.rows || []);
  const customerType = useSelector((state) => state.customerType);

  const employeeId = me?.userData?._id;

  const [fuelExpenses, setFuelExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expPage, setExpPage] = useState(0);
  const [expSize, setExpSize] = useState(10);
  const [showDupeInvoice, setShowDupeInvoice] = useState(false);

  const vehicle = useVehicle({ employeeId, employees, dispatch });
  const claim = useFuelClaim({ me, dispatch });

  const fetchFuelExpenses = useCallback(async () => {
    if (!employeeId) return;
    setExpensesLoading(true);
    try {
      const q = new URLSearchParams({ me: employeeId, size: 999, page: 1 });
      const { data, status } = await api.get(
        `${process.env.REACT_APP_API_URL}/expenses?${q.toString()}`,
      );
      // เอาเฉพาะรายการเบิกน้ำมัน: ชื่อรายการมีคำว่า "น้ำมัน" หรือมี fuel_station+fuel_liters
      // (สอง field นี้เซ็ตเฉพาะตอนสร้างจากฟอร์ม "เติมน้ำมัน" ในหน้านี้เท่านั้น) — กันรายการเบิกปกติ
      // จากหน้า /profile/disbursement หลุดเข้ามาปนในตารางนี้
      if (status === 200) {
        setFuelExpenses(
          (data?.rows || []).filter(
            (e) => Boolean(e?.name?.includes('น้ำมัน'))
              || (e?.fuel_station != null && e?.fuel_liters != null),
          ),
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setExpensesLoading(false);
    }
  }, [employeeId]);

  const dupeInvoiceExpenses = useMemo(() => {
    const withInvoice = fuelExpenses.filter((e) => e.invoice_number?.trim());
    const counts = {};
    withInvoice.forEach((e) => { counts[e.invoice_number.trim()] = (counts[e.invoice_number.trim()] || 0) + 1; });
    return fuelExpenses.filter((e) => e.invoice_number?.trim() && counts[e.invoice_number.trim()] > 1);
  }, [fuelExpenses]);

  useEffect(() => {
    if (employeeId) dispatch(actions.vehicleFuelSettingGet(employeeId));
    dispatch(actions.employeeAll({ size: 9999, activeOnly: false }));
    dispatch(actions.projectAll({ size: 999, page: 1, lite: true }));
    dispatch(actions.customerAll({}));
    dispatch(actions.customerTypeAll({}));
    vehicle.fetchCompanyVehicles();
  }, [employeeId]);

  useEffect(() => { fetchFuelExpenses(); }, [fetchFuelExpenses]);

  useEffect(() => {
    if (vehicleFuelSetting) vehicle.setVehicles(vehicleFuelSetting.vehicles || []);
  }, [vehicleFuelSetting]);

  useEffect(() => {
    if (!claim.claimForm.project?._id) return;
    dispatch(actions.budgetAll({ project_id: claim.claimForm.project._id, size: 999, page: 1 }));
  }, [claim.claimForm.project]);

  useEffect(() => {
    if (!claim.claimForm.project || claim.claimForm.budget) return;
    const fuelBudget = budgets.find((b) =>
      (b.name || '').toLowerCase().includes('น้ำมัน') ||
      (b.name || '').toLowerCase().includes('fuel'),
    );
    if (fuelBudget) claim.setClaimForm((prev) => ({ ...prev, budget: fuelBudget }));
  }, [budgets]);

  const visibleExpenses = showDupeInvoice ? dupeInvoiceExpenses : fuelExpenses;

  // Stable reference so <ExpensesTable> (memoized) doesn't re-render on every
  // keystroke in the fuel-claim modal above it — its own state changes don't
  // affect this table, but an inline object literal here would still break memo.
  const expensesTableData = useMemo(
    () => ({ rows: visibleExpenses, total: visibleExpenses.length, isLoading: expensesLoading }),
    [visibleExpenses, expensesLoading],
  );

  return (
    <Box sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>{title || 'การตั้งเบิกน้ำมันรถ'}</Typography>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<FuelIcon />}
            onClick={claim.openClaim}
            disabled={vehicle.allVehiclesForClaim.length === 0}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            ตั้งเบิกน้ำมัน
          </Button>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<CarIcon />}
            onClick={() => vehicle.setListOpen(true)}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            + เพิ่มรถ
          </Button>
        </Stack>
      </Box>

      {vehicle.saved && <Alert severity="success" className="mb-3">บันทึกข้อมูลรถยนต์เรียบร้อยแล้ว</Alert>}
      {vehicle.saveError && <Alert severity="error" className="mb-3">บันทึกไม่สำเร็จ: {vehicle.saveError}</Alert>}
      {claim.claimSuccess && <Alert severity="success" className="mb-3">สร้างรายการเบิกน้ำมันเรียบร้อยแล้ว</Alert>}

      {vehicle.allVehiclesForClaim.length === 0 && (
        <Alert severity="info">ยังไม่มีรถยนต์ในระบบ กด "จัดการรถยนต์" เพื่อเพิ่มรถก่อนตั้งเบิก</Alert>
      )}

      {/* รายการตั้งเบิกน้ำมัน */}
      <Box className="mt-4">
        <Box className="flex items-center justify-between mb-2">
          <Box className="flex items-center gap-2">
            <FuelIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>รายการตั้งเบิกน้ำมัน</Typography>
            <Chip label={fuelExpenses.length} size="small" color="primary" variant="outlined" />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<DupeIcon sx={{ fontSize: '15px !important' }} />}
              label={`ใบกำกับซ้ำ${dupeInvoiceExpenses.length > 0 ? ` (${dupeInvoiceExpenses.length})` : ''}`}
              size="small"
              color={showDupeInvoice ? 'error' : 'default'}
              variant={showDupeInvoice ? 'filled' : 'outlined'}
              onClick={() => { setShowDupeInvoice((v) => !v); setExpPage(0); }}
              sx={{ cursor: 'pointer', fontWeight: showDupeInvoice ? 700 : 400 }}
            />
            <IconButton size="small" onClick={fetchFuelExpenses} disabled={expensesLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Divider className="mb-2" />
        {showDupeInvoice && dupeInvoiceExpenses.length === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>ไม่พบรายการที่มีเลขใบกำกับภาษีซ้ำกัน</Alert>
        )}
        <ExpensesTable
          expenses={expensesTableData}
          filter={visibleExpenses}
          page={expPage}
          size={expSize}
          setPage={setExpPage}
          setSize={setExpSize}
        />
      </Box>

      <VehicleListDialog
        open={vehicle.listOpen}
        onClose={() => vehicle.setListOpen(false)}
        vehicles={vehicle.vehicles}
        otherCompanyVehicles={vehicle.otherCompanyVehicles}
        employees={employees}
        openAdd={vehicle.openAdd}
        openEdit={vehicle.openEdit}
        handleSetDefault={vehicle.handleSetDefault}
        handleDeleteVehicle={vehicle.handleDeleteVehicle}
      />

      <VehicleFormDialog
        open={vehicle.formOpen}
        onClose={() => vehicle.setFormOpen(false)}
        vehicleForm={vehicle.vehicleForm}
        setVehicleForm={vehicle.setVehicleForm}
        editIdx={vehicle.editIdx}
        employees={employees}
        onSave={vehicle.handleFormSave}
      />

      <FuelClaimDialog
        open={claim.claimOpen}
        onClose={() => claim.setClaimOpen(false)}
        claimForm={claim.claimForm}
        setClaimForm={claim.setClaimForm}
        formErrors={claim.formErrors}
        setFormErrors={claim.setFormErrors}
        ocrResult={claim.ocrResult}
        setOcrResult={claim.setOcrResult}
        ocrScanning={claim.ocrScanning}
        ocrOdoScanning={claim.ocrOdoScanning}
        claimSubmitting={claim.claimSubmitting}
        uploadProgress={claim.uploadProgress}
        needsOdo={claim.needsOdo}
        allVehiclesForClaim={vehicle.allVehiclesForClaim}
        employees={employees}
        projects={projects}
        budgets={budgets}
        customers={customers}
        customerType={customerType}
        handleScanBill={claim.handleScanBill}
        handleScanOdometer={claim.handleScanOdometer}
        handleProjectChange={claim.handleProjectChange}
        handleBudgetChange={claim.handleBudgetChange}
        handleVehicleChange={claim.handleVehicleChange}
        handleCustomerChange={claim.handleCustomerChange}
        handleExpensesTypeChange={claim.handleExpensesTypeChange}
        handlePaymentDateChange={claim.handlePaymentDateChange}
        handleOdoChange={claim.handleOdoChange}
        handleDateChange={claim.handleDateChange}
        handleAmountChange={claim.handleAmountChange}
        customerFilterOptions={claim.customerFilterOptions}
        onSubmit={() => claim.handleClaimSubmit(fetchFuelExpenses)}
        openCustomerModal={claim.openCustomerModal}
        handleCloseCustomerModal={claim.handleCloseCustomerModal}
        bank={claim.bank}
        setBank={claim.setBank}
        customerSubmitting={claim.customerSubmitting}
        customerControl={claim.customerControl}
        customerErrors={claim.customerErrors}
        handleCustomerSubmit={claim.handleCustomerSubmit}
        onSubmitCustomer={claim.onSubmitCustomer}
      />
    </Box>
  );
}
